"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Badge } from "@/components/ui/badge";
import { Lock, Terminal, Loader } from "lucide-react";
function TerminalForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="223540" className="p-6 md:p-8 font-default text-sm space-y-4">
                <p className="text-primary font-semibold">WELCOME TO THE CLUB</p>
                <p className="text-muted-foreground">{successMessage}</p>
                <p className="text-primary animate-pulse">●</p>
            </div>;
  }
  return <Form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-6 font-default text-sm">
            <div className="space-y-4">
                <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem className="space-y-1">
                            <FormLabel className="text-muted-foreground flex items-center gap-2 font-default text-xs uppercase tracking-widest">
                                <span className="text-primary">/</span> Full Name
                            </FormLabel>
                            <FormControl>
                                <div className="flex items-center gap-2">
                                    <span className="text-muted-foreground/30">→</span>
                                    <Input {...field} placeholder="Arthur Artisan" className="border-0 border-b border-primary/20 rounded-none bg-transparent px-0 h-8 focus-visible:ring-0 focus-visible:border-primary text-foreground placeholder:text-muted-foreground/40 font-default shadow-none" />
                                </div>
                            </FormControl>
                            <FormMessage className="text-xs" />
                        </FormItem>} />

                <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^\S+@\S+$/i,
          message: "Invalid email format"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-1">
                            <FormLabel className="text-muted-foreground flex items-center gap-2 font-default text-xs uppercase tracking-widest">
                                <span className="text-primary">/</span> Email Address
                            </FormLabel>
                            <FormControl>
                                <div className="flex items-center gap-2">
                                    <span className="text-muted-foreground/30">→</span>
                                    <Input {...field} type="email" placeholder="hello@crust.com" className="border-0 border-b border-primary/20 rounded-none bg-transparent px-0 h-8 focus-visible:ring-0 focus-visible:border-primary text-foreground placeholder:text-muted-foreground/40 font-default shadow-none" />
                                </div>
                            </FormControl>
                            <FormMessage className="text-xs" />
                        </FormItem>} />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField control={control} name="organization" render={({
          field
        }) => <FormItem className="space-y-1">
                                <FormLabel className="text-muted-foreground flex items-center gap-2 font-default text-xs uppercase tracking-widest">
                                    <span className="text-primary">/</span> Neighborhood
                                </FormLabel>
                                <FormControl>
                                    <div className="flex items-center gap-2">
                                        <span className="text-muted-foreground/30">→</span>
                                        <Input {...field} placeholder="West End" className="border-0 border-b border-primary/20 rounded-none bg-transparent px-0 h-8 focus-visible:ring-0 focus-visible:border-primary text-foreground placeholder:text-muted-foreground/40 font-default shadow-none" />
                                    </div>
                                </FormControl>
                                <FormMessage className="text-xs" />
                            </FormItem>} />

                    <FormField control={control} name="role" render={({
          field
        }) => <FormItem className="space-y-1">
                                <FormLabel className="text-muted-foreground flex items-center gap-2 font-default text-xs uppercase tracking-widest">
                                    <span className="text-primary">/</span> Favorite Loaf
                                </FormLabel>
                                <FormControl>
                                    <div className="flex items-center gap-2">
                                        <span className="text-muted-foreground/30">→</span>
                                        <Input {...field} placeholder="Sourdough" className="border-0 border-b border-primary/20 rounded-none bg-transparent px-0 h-8 focus-visible:ring-0 focus-visible:border-primary text-foreground placeholder:text-muted-foreground/40 font-default shadow-none" />
                                    </div>
                                </FormControl>
                                <FormMessage className="text-xs" />
                            </FormItem>} />
                </div>

                <FormField control={control} name="topics" render={({
        field
      }) => <FormItem className="space-y-3 pt-2">
                            <FormLabel className="text-muted-foreground flex items-center gap-2 font-default text-xs uppercase tracking-widest">
                                <span className="text-primary">/</span> Alert Preferences
                            </FormLabel>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pl-4 border-l border-primary/10">
                                {["5am Batch Alerts", "Weekend Specials", "Baking Workshops", "Seasonal Pastries", "Local Events"].map((option, i) => <label key={i} data-index={i} className="flex items-center gap-3 cursor-pointer group">
                                        <div className="relative flex items-center justify-center size-4 rounded-sm border border-primary/30 group-hover:border-primary transition-colors">
                                            {field.value?.includes(option) && <div className="size-2 bg-primary rounded-sm" />}
                                        </div>
                                        <input type="checkbox" className="sr-only" checked={field.value?.includes(option)} onChange={e => {
              const current = field.value || [];
              const updated = e.target.checked ? [...current, option] : current.filter((val: string) => val !== option);
              field.onChange(updated);
            }} />
                                        <span className="text-xs text-muted-foreground group-hover:text-foreground transition-colors font-default">{option}</span>
                                    </label>)}
                            </div>
                            <FormMessage className="text-xs" />
                        </FormItem>} />

                <FormField control={control} name="message" render={({
        field
      }) => <FormItem className="space-y-1 pt-2">
                            <FormLabel className="text-muted-foreground flex items-center gap-2 font-default text-xs uppercase tracking-widest">
                                <span className="text-primary">/</span> Special Requests
                            </FormLabel>
                            <FormControl>
                                <div className="flex gap-2">
                                    <span className="text-muted-foreground/30 mt-2">→</span>
                                    <textarea {...field} placeholder="Any dietary preferences or custom order inquiries?" className="flex min-h-[60px] w-full border-0 border-b border-primary/20 rounded-none bg-transparent px-0 py-2 text-sm focus-visible:outline-none focus-visible:border-primary text-foreground placeholder:text-muted-foreground/40 resize-none font-default" />
                                </div>
                            </FormControl>
                            <FormMessage className="text-xs" />
                        </FormItem>} />
            </div>

            {submitError && <div className="p-3 border border-destructive/50 bg-destructive/5 text-destructive text-xs font-default">
                    ERROR: {submitError}
                </div>}

            <div className="pt-4">
                <Button type="submit" disabled={isSubmitting} variant="default" className="w-full font-default text-sm font-semibold tracking-widest rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 shadow-md transition-all py-6">
                    {isSubmitting ? <>
                            <Loader className="mr-2 size-4 animate-spin" />
                            PREPARING...
                        </> : 'JOIN THE CRUST CLUB'}
                </Button>
            </div>
        </Form>;
}
export default function StrategicUpdates() {
  return <section id="join-the-crust-club" className="bg-background text-foreground py-24 px-4 relative overflow-hidden">
            {}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,var(--color-primary)_0.5px,transparent_0.5px)] bg-[size:24px_24px] opacity-[0.03] pointer-events-none" />
            
            <div className="max-w-2xl mx-auto relative z-10">
                <div className="text-center mb-12 space-y-4">
                    <h2 className="text-5xl md:text-6xl font-default font-bold text-foreground tracking-tight">Fresh From the Oven</h2>
                    <p className="text-lg text-muted-foreground font-default max-w-md mx-auto">Join the Crust Club for 5am batch alerts and exclusive seasonal treats.</p>
                </div>

                {}
                <div className="rounded-xl border border-border bg-card/50 backdrop-blur-xl shadow-2xl shadow-primary/5 overflow-hidden">
                    {}
                    <div className="bg-muted/20 px-4 py-3 flex items-center justify-between border-b border-border">
                        <div className="flex items-center gap-2">
                            <div className="size-2.5 rounded-full bg-primary/20" />
                            <div className="size-2.5 rounded-full bg-primary/40" />
                            <div className="size-2.5 rounded-full bg-primary/60" />
                        </div>
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-default font-bold text-muted-foreground">
                            <Terminal className="size-3 text-primary" />
                            <span>Crust_Member_Portal</span> 
                        </div>
                        <div className="w-10" /> {}
                    </div>

                    {}
                    <FormProvider formKey="crust_club_signup_home" formId="crust_club_signup_home" sectionName="Strategic Updates" defaultValues={{
          name: "",
          email: "",
          organization: "",
          role: "",
          topics: [],
          message: ""
        }} validationRules={{
          name: {
            required: "Name is required"
          },
          email: {
            required: "Email is required",
            pattern: {
              value: /^\S+@\S+$/i,
              message: "Invalid email format"
            }
          }
        }} submitText="JOIN THE CRUST CLUB" successMessage="Welcome to the club. You'll hear from us at the first crack of dawn.">
                        <TerminalForm />
                    </FormProvider>
                </div>

                {}
                <div className="mt-8 flex justify-center">
                    <Badge variant="outline" className="font-default text-[10px] uppercase tracking-[0.15em] border-primary/10 text-primary/60 gap-2 bg-background/40 backdrop-blur-sm py-1.5 px-4 rounded-full">
                        <Lock className="size-3" />
                        SECURE_MEMBER_ENROLLMENT
                    </Badge>
                </div>
            </div>
        </section>;
}